import { Button, Col, Form, Input, Row, Spin, Switch } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { useContext, useState } from 'react';
import ReactQuill from 'react-quill';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import axiosInstance from '../../../../../../../axios_instance';
import {
  IsAdminContext,
  NumberOfExperienceContext,
  UserExperienceDataContext,
} from '../../../../../../../context';
import { errorHandling } from '../../../../../../../utils/helper';
import {
  EndDateValidator,
  StartDateValidator,
  TextLengthValidator,
} from '../../../../../../../utils/validators';
import CheckBoxComponent from '../../../../../check-box';
import styles from './styles.module.scss';

const StyledInput = styled(Input)`
  ::-webkit-calendar-picker-indicator {
    content: url(/calendar_today.png);
    background: #f6f7fc;
  }
`;

interface EditExperienceProps {
  experience_id: number;
  company_name: string;
  job_responsibility: string;
  designation: any;
  start_date: number;
  end_date: number;
  setIsEditable: any;
  show_in_resume: boolean;
}

const EditExperience: React.FC<EditExperienceProps> = ({
  experience_id,
  company_name,
  designation,
  start_date,
  end_date,
  job_responsibility,
  setIsEditable,
  show_in_resume
}) => {
  const [formData, setFormData] = useState<{ [key: string]: number | string }>({
    experience_id,
    company_name,
    job_responsibility,
    designation: designation['designation'],
    start_date,
    end_date,
  });
  const { userExperienceData, setUserExperienceData } = useContext(
    UserExperienceDataContext
  );
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const isAdmin = useContext(IsAdminContext);
  const { employeeId } = useParams();
  const [isCurrentlyWorking, setIsCurrentlyWorking] = useState<boolean>(
    !end_date
  );
  const { numberOfExperience, setNumberOfExperience } = useContext(
    NumberOfExperienceContext
  );

  const handleCurrentWorkingCheckbox = (e: CheckboxChangeEvent) => {
    setIsCurrentlyWorking(e.target.checked);
  };

  const handleChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value.trim(),
    });
  };

  const handleChangeResume = (value: any) => {
    setFormData({
      ...formData,
      ["show_in_resume"]: value,
    });
  };

  const handleChangeText = (value: any, name: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = () => {
    setIsSubmitted(true);
    const requestBody: { [key: string]: any } = {};
    requestBody['experience_id'] = formData['experience_id'];
    requestBody['company_name'] = formData['company_name'];
    requestBody['job_responsibility'] = formData['job_responsibility'];
    requestBody['designation'] = formData['designation'];
    requestBody['start_date'] = formData['start_date'];
    requestBody['show_in_resume'] = 'show_in_resume' in formData ? formData['show_in_resume'] : show_in_resume;
    requestBody['end_date'] = isCurrentlyWorking ? null : formData['end_date'];
    requestBody['status'] = formData['1'];
    if (
      requestBody['company_name'] !== company_name ||
      requestBody['job_responsibility'] !== job_responsibility ||
      requestBody['designation'] !== designation['designation'] ||
      requestBody['start_date'] !== start_date ||
      requestBody['end_date'] !== end_date ||
      requestBody['show_in_resume'] !== show_in_resume
    ) {
      let url = '';
      if (isAdmin === '1') {
        url = '/api/v1/admin/profile/experiences';
        requestBody['profile_id'] = employeeId;
      } else {
        url = '/api/v1/profile/experiences';
      }
      axiosInstance
        .post(url, requestBody)
        .then((response) => {
          const orderedExperiences = response.data.order
            .map((experienceId: number) =>
              response.data.experiences.find(
                (experience: any) => experience.experience_id === experienceId
              )
            )
            .filter((item: any) => item !== undefined);
          setUserExperienceData(orderedExperiences);
          setNumberOfExperience(orderedExperiences.length);
          setIsEditable(false);
        })
        .catch((error) => {
          errorHandling(error, setErrorMessage);
        })
        .finally(() => {
          setIsSubmitted(false);
        });
    } else {
      setIsEditable(false);
    }
  };

  return (
    <div>
      <Form onFinish={handleSave} scrollToFirstError>
        <Row className={styles.row} style={{ paddingBottom: "50px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", position: "absolute", bottom: "8px", right: "8px" }}><Switch defaultChecked={show_in_resume} onChange={handleChangeResume} /><span>Show in resume</span></div>
          <Col span={12}>
            <Row>
              <Form.Item
                name="company_name"
                label=""
                rules={[TextLengthValidator]}
                initialValue={company_name}
              >
                <p>
                  <span className={styles.title}>Company Name : </span>
                  <Input
                    type="text"
                    placeholder="Write Company Name Here"
                    className={styles.input}
                    name="company_name"
                    onChange={handleChange}
                    defaultValue={company_name}
                    style={{
                      borderBottom:
                        'company_name' in errorMessage ? '1px solid red' : '',
                    }}
                  />
                </p>
              </Form.Item>
            </Row>
            <Row>
              <p style={{ color: 'red' }}>
                {'company_name' in errorMessage
                  ? errorMessage['company_name']
                  : ''}
              </p>
            </Row>
            <Row>
              <Form.Item
                name="designation"
                label=""
                rules={[TextLengthValidator]}
                initialValue={designation['designation']}
              >
                <p>
                  <span className={styles.title}>Designation : </span>
                  <Input
                    type="text"
                    placeholder="Write Designation Here"
                    className={styles.input}
                    name="designation"
                    onChange={handleChange}
                    defaultValue={designation['designation']}
                    style={{
                      borderBottom:
                        'designation' in errorMessage ? '1px solid red' : '',
                    }}
                  />
                </p>
              </Form.Item>
            </Row>
          </Col>
          <Col span={12}>
            <Row>
              <Form.Item
                name="start_date"
                label=""
                initialValue={start_date}
                rules={[StartDateValidator]}
              >
                <p>
                  <span className={styles.title}>From : </span>
                  <StyledInput
                    className={styles['input-small']}
                    onChange={handleChange}
                    name="start_date"
                    type="date"
                    defaultValue={start_date}
                    style={{
                      borderBottom:
                        'start_date' in errorMessage ? '1px solid red' : '',
                    }}
                  />
                </p>
              </Form.Item>
            </Row>
            <Row>
              <p style={{ color: 'red' }}>
                {'start_date' in errorMessage ? errorMessage['start_date'] : ''}
              </p>
            </Row>
            <Row>
              <Form.Item
                name="end_date"
                label=""
                dependencies={['start_date']}
                initialValue={end_date}
                rules={[EndDateValidator]}
              >
                <p>
                  <span className={styles.title}>To : </span>
                  <StyledInput
                    disabled={isCurrentlyWorking}
                    className={styles['input-small']}
                    onChange={handleChange}
                    name="end_date"
                    type="date"
                    defaultValue={end_date}
                    style={{
                      borderBottom:
                        'end_date' in errorMessage ? '1px solid red' : '',
                    }}
                  />
                </p>
              </Form.Item>
              <Form.Item name="present" label="" dependencies={['start_date']}>
                <CheckBoxComponent
                  text={'Present'}
                  checked={!end_date ? true : false}
                  onChange={handleCurrentWorkingCheckbox}
                />
              </Form.Item>
            </Row>
            <Row>
              <p style={{ color: 'red' }}>
                {'end_date' in errorMessage ? errorMessage['end_date'] : ''}
              </p>
            </Row>
          </Col>
          <Col span={24}>
            <div>
              <Form.Item
                name="job_responsibility"
                label=""
                rules={[TextLengthValidator]}
                initialValue={job_responsibility}
              >
                <p>
                  <span className={styles.title}>Responsibilities : </span>
                  <ReactQuill theme="snow"
                    defaultValue={job_responsibility}
                    placeholder="Write roles and responsibilities here"
                    style={{ height: "180px" }}
                    onChange={(val) => handleChangeText(val, "job_responsibility")}
                    className={styles.inputTextArea} />
                </p>
              </Form.Item>
            </div>
            <Row>
              <p style={{ color: 'red' }}>
                {'job_responsibility' in errorMessage
                  ? errorMessage['job_responsibility']
                  : ''}
              </p>
            </Row>
          </Col>
        </Row>
        <Row>
          {isSubmitted ? <Spin /> : <></>}
          <Form.Item style={{ float: 'right', marginLeft: 'auto' }}>
            <Button
              className={styles['save-button']}
              style={{ float: 'right', marginLeft: 'auto', marginRight: '0px' }}
              type="primary"
              htmlType="submit"
            >
              Save
            </Button>
          </Form.Item>
          <Button
            className={styles['cancel-button']}
            style={{ marginRight: '0px' }}
            onClick={(e) => {
              setIsEditable(false);
            }}
          >
            Cancel
          </Button>
        </Row>
      </Form>
    </div>
  );
};

export default EditExperience;
